import type { AccountInfo } from "@azure/msal-browser";
import { resolveAccountOid, userAzureId, userOid } from "@/helpers/auth";
import { useAuthStore } from "@/store/authStore";
import { useIcs4Store } from "@/store/storeIcs4";
import { useUserContextStore } from "@/store/userContextStore";
import { useGlobalAdminStore } from "@/store/global-admin/globalAdminStore";
import { useProjectContextStore } from "@/store/projectContextStore";
import { useCurrentProjectStore } from "@/store/currentProjectStore";
import { useUserCapabilitiesStore } from "@/store/userCapabilitiesStore";
import { useNotificationsStore } from "@/store/notificationsStore";

// Sist synkede konto-oid. Brukes til å oppdage et identitets-BYTTE i samme fane
// (logout→ny innlogging uten reload), som ellers ville la forrige brukers
// session-scopede tilstand henge igjen (WI 2255).
let lastSyncedOid = "";

/**
 * Nullstill all identitets-/session-scopet Pinia-tilstand. Kalles ved
 * identitetsendring — logout (`useAuthShell.logout`) og konto-bytte i
 * `syncSessionStores` — slik at neste bruker i samme fane starter rent og
 * ingen tilgangsvakt (router-guard) leser et utdatert flagg fra forrige sesjon.
 *
 * Egen modul (ikke i useAuthShell) for å unngå sirkulær import
 * providerRegistry ↔ useAuthShell. Stores hentes via `useXStore()` inne i
 * funksjonen (ikke på modulnivå) så Pinia er aktiv ved kall.
 */
export function resetSessionStores (): void {
  useAuthStore().reset();
  useIcs4Store().reset();
  useUserContextStore().reset();
  useGlobalAdminStore().reset();
  useProjectContextStore().reset();
  useCurrentProjectStore().reset();
  useUserCapabilitiesStore().reset();
  userAzureId.value = "";
  userOid.value = "";
  lastSyncedOid = "";
}

/**
 * Synkroniser delte Pinia-stores + globale refs som template/komponenter leser,
 * fra en autentisert MSAL-konto.
 *
 * Brukes både etter popup-login (`useAuthShell.login`) og etter redirect-retur
 * (`providerRegistry.handleShellRedirectResponse`) slik at begge påloggings-stiene
 * lander i samme innloggede UI-tilstand (WI 2196). Tidligere synket bare popup-
 * stien, så en B2C-redirect-retur lot `authStore.isAuthenticated` stå false og UI
 * viste fortsatt påloggingsvalgene.
 *
 * WI 2255: oppdager et identitets-BYTTE (ny konto-oid ≠ forrige) og nullstiller
 * forrige brukers session-tilstand FØR re-synk. Samme oid (warm-restore /
 * oppfriskning) lar den gyldige sesjonen stå urørt.
 *
 * Egen modul (ikke i useAuthShell) for å unngå sirkulær import
 * providerRegistry ↔ useAuthShell. No-op ved manglende konto.
 */
export function syncSessionStores (account: AccountInfo | null): void {
  if (!account) {
    return;
  }
  const oid = resolveAccountOid(account);
  if (lastSyncedOid !== "" && oid !== lastSyncedOid) {
    resetSessionStores();
  }
  const authStore = useAuthStore();
  const ics4Store = useIcs4Store();
  authStore.setAuthStatus(true, account.name ?? null);
  ics4Store.isAuth = true;
  if (account.name) {
    ics4Store.userName = account.name;
  }
  userAzureId.value = account.username ?? "";
  userOid.value = oid;
  lastSyncedOid = oid;

  // Bug 2970: etabler en bruker-scopet SignalR-kobling så snart identiteten er kjent
  // (popup-/redirect-innlogging), slik at e-postutfall og andre user_{oid}-pushes når en
  // GA i tilgangs-/modul-området eller en søker uten prosjekt. Prosjekt-åpning re-initer
  // med dbName; en levende kobling nedgraderes ikke (se initializeConnection). Fire-and-
  // forget — koblingen skal ALDRI blokkere eller velte innloggingssynkroniseringen, så
  // både en synkron oppstartsfeil og en avvist promise logges og svelges.
  try {
    useNotificationsStore().initializeConnection().catch((err: unknown) => {
      console.error("[sessionSync] Kunne ikke etablere bruker-scopet SignalR-kobling:", err);
    });
  } catch (connError) {
    console.error("[sessionSync] Feil ved oppstart av bruker-scopet SignalR-kobling:", connError);
  }
}

/** Kun for tester — nullstill modul-intern oid-sporing mellom test-tilfeller. */
export function _resetSessionSyncForTests (): void {
  lastSyncedOid = "";
}
